import React, { useRef, useEffect, useState } from 'react';
import { principles, executionStats } from '../../data/values';
import useWordReveal from '../../hooks/useWordReveal';
import { PipelineArrows, ConnectionDots, GearOutline, ShieldLock, BinaryCode } from '../../components/Doodle/Doodle';
import { IsoRocket, IsoNetwork } from '../../components/Svg3D/Svg3D';
import styles from './HowWeWork.module.css';

const AnimatedStat = ({ value, isVisible }) => {
  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    if (!isVisible) return;

    const match = value.match(/^([^0-9]*)([0-9.]+)(.*)$/);
    if (!match) { setDisplayValue(value); return; }

    const prefix = match[1];
    const target = parseFloat(match[2]);
    const suffix = match[3];
    const decimals = match[2].includes('.') ? match[2].split('.')[1].length : 0;
    const duration = 1500;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = (target * eased).toFixed(decimals);
      setDisplayValue(`${prefix}${current}${suffix}`);
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [isVisible, value]);

  return <span>{displayValue}</span>;
};

const HowWeWork = () => {
  const [headingRef, isVisible] = useWordReveal({ threshold: 0.3 });
  const cardsRef = useRef(null);
  const statsRef = useRef(null);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);

  const headingText = 'Embedded Execution, Not Outsourced Delivery';
  const words = headingText.split(' ');

  useEffect(() => {
    const node = cardsRef.current;
    const statsNode = statsRef.current;

    const cardsObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCardsVisible(true);
          cardsObserver.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    const statsObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
          statsObserver.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (node) cardsObserver.observe(node);
    if (statsNode) statsObserver.observe(statsNode);

    return () => {
      cardsObserver.disconnect();
      statsObserver.disconnect();
    };
  }, []);

  return (
    <section id="how-we-work" className={styles.section}>
      <PipelineArrows position={{ top: '4%', right: '3%' }} size={160} theme="dark" opacity={0.15} className={styles.doodle} />
      <ConnectionDots position={{ bottom: '8%', left: '2%' }} size={140} theme="dark" opacity={0.14} className={styles.doodle} />
      <GearOutline position={{ top: '35%', left: '1%' }} size={120} theme="dark" opacity={0.11} className={styles.doodle} />
      <ShieldLock position={{ bottom: '5%', right: '2%' }} size={130} theme="dark" opacity={0.12} className={styles.doodle} />
      <BinaryCode position={{ top: '8%', left: '3%' }} size={100} theme="dark" opacity={0.10} className={styles.doodle} />
      <IsoRocket position={{ top: '18%', right: '7%' }} size={150} opacity={0.5} />
      <IsoNetwork position={{ bottom: '20%', right: '5%' }} size={140} opacity={0.45} />
      <div className="container">
        <div className={styles.header}>
          <span className={styles.sectionTag}>HOW WE WORK</span>
          <div
            ref={headingRef}
            className={`word-reveal${isVisible ? ' visible' : ''}`}
          >
            <h2 className={styles.heading}>
              {words.map((word, i) => (
                <span
                  key={i}
                  className="word"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  {word}
                </span>
              ))}
            </h2>
          </div>
          <p className={styles.subtitle}>
            We're not a vendor selling products or pushing frameworks. We're an
            execution partner embedded in your success, sharing accountability
            for outcomes.
          </p>
        </div>

        <div
          ref={cardsRef}
          className={styles.cardsGrid}
        >
          {principles.map((principle, index) => {
            const Icon = principle.icon;
            return (
              <div
                key={index}
                className={`${styles.card} ${cardsVisible ? styles.cardVisible : ''}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className={styles.cardTop}>
                  <span className={styles.cardNumber}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className={styles.cardIcon}>
                    <Icon size={22} />
                  </span>
                </div>
                <h3 className={styles.cardTitle}>{principle.title}</h3>
                <p className={styles.cardDescription}>{principle.description}</p>
              </div>
            );
          })}
        </div>

        <div ref={statsRef} className={styles.statsRow}>
          {executionStats.map((stat, index) => (
            <React.Fragment key={index}>
              {index > 0 && <div className={styles.statDivider} />}
              <div className={styles.statItem}>
                <span className={styles.statValue}><AnimatedStat value={stat.value} isVisible={statsVisible} /></span>
                <span className={styles.statLabel}>{stat.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowWeWork;
